import React, { useState } from 'react';
import {
  Box, Typography, Stepper, Step, StepLabel, Card, CardContent, Grid,
  TextField, MenuItem, Button, Divider, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControlLabel, Checkbox, InputAdornment,
  Radio, RadioGroup, FormControl, FormLabel, Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import FileUpload from '../../components/common/FileUpload';
import { useAuth } from '../../context/AuthContext';
import db from '../../data/demoDatabase';

const steps = ['Personal Details', 'Product Selection', 'Documents', 'Review & Submit'];

const availableProducts = [
  { id: 1, name: 'Al Rajhi Personal Financing-i', bank: 'Al Rajhi Bank', rate: '5.27% p.a.', maxAmount: 250000 },
  { id: 2, name: 'Public Islamic BAE Financing-i', bank: 'Public Islamic Bank', rate: '3.99% p.a.', maxAmount: 150000 },
  { id: 3, name: 'CBP Financing-i Al Yasser', bank: 'CIMB Bank', rate: '2.80% p.a.', maxAmount: 400000 },
  { id: 4, name: 'Bank Rakyat Financing-i', bank: 'Bank Rakyat', rate: '3.42% p.a.', maxAmount: 400000 },
];

export default function ApplicationForm() {
  const [activeStep, setActiveStep] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedCase, setSubmittedCase] = useState(null);
  const [tokenAwarded, setTokenAwarded] = useState(0);
  const { user, isAuthenticated, updateUser } = useAuth();
  const navigate = useNavigate();

  const handleSubmitApplication = () => {
    const product = availableProducts.find((p) => String(p.id) === form.selectedProduct);
    // Resolve referral owner via referral code (match against any user.referralCode)
    let referralOwnerId = null;
    if (form.referralCode || refCode) {
      const code = form.referralCode || refCode;
      const allUsers = db.users.getAll();
      const owner = allUsers.find(
        (u) => u.referralCode && u.referralCode.toLowerCase() === code.toLowerCase()
      );
      if (owner && owner.id !== user?.id) referralOwnerId = owner.id;
    }
    const newCase = db.engine.submitCase(
      {
        applicantId: user?.id,
        applicantName: form.fullName || user?.name,
        productId: product?.id || 'PROD001',
        productName: product?.name || 'Personal Financing-i',
        bank: product?.bank || 'iMoney',
        amount: Number(String(form.amount).replace(/,/g, '')) || 0,
        referralCode: form.referralCode || refCode || null,
        referralType: referralOwnerId ? 'referral' : 'self',
        referralOwnerId,
      },
      user
    );
    setSubmittedCase(newCase);

    // Refresh local user state to reflect token awarded
    if (user?.id) {
      const refreshed = db.users.getById(user.id);
      if (refreshed) {
        const tokensBefore = Number(user.tokenBalance) || 0;
        const tokensAfter = Number(refreshed.tokenBalance) || 0;
        setTokenAwarded(tokensAfter - tokensBefore);
        // Persist updated user back to auth context so other pages refresh
        updateUser?.({ tokenBalance: refreshed.tokenBalance });
      }
    }

    setShowSuccess(true);
  };
  const [form, setForm] = useState({
    fullName: '', icNumber: '', phone: '', email: '', monthlyIncome: '',
    employmentType: '', employer: '', state: '', referralCode: '',
    selectedProduct: '', amount: '', tenure: '1',
    isCitizen: true, consent: false, smartAI: true,
  });

  const urlParams = new URLSearchParams(window.location.search);
  const refCode = urlParams.get('ref') || '';

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const renderStep0 = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Alert severity="info" sx={{ borderRadius: 2, mb: 1 }}>
          Fill in your details to apply. All fields marked with * are required.
        </Alert>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Full Name *" value={form.fullName} onChange={update('fullName')} placeholder="Mohammad Johan bin Ismail" />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="IC Number *" value={form.icNumber} onChange={update('icNumber')} placeholder="940221025055" />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Email Address *" value={form.email} onChange={update('email')} placeholder="mohammad@email.com" type="email" />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Phone Number *" value={form.phone} onChange={update('phone')}
          InputProps={{ startAdornment: <InputAdornment position="start">+6</InputAdornment> }}
          placeholder="01234567891"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Monthly Income *" value={form.monthlyIncome} onChange={update('monthlyIncome')}
          InputProps={{ startAdornment: <InputAdornment position="start">RM</InputAdornment> }}
          placeholder="5,000"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth select label="Employment Type *" value={form.employmentType} onChange={update('employmentType')}>
          <MenuItem value="employed">Employed</MenuItem>
          <MenuItem value="self_employed">Self-Employed</MenuItem>
          <MenuItem value="government">Government Servant</MenuItem>
          <MenuItem value="retired">Retired</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Employer Name" value={form.employer} onChange={update('employer')} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth select label="State *" value={form.state} onChange={update('state')}>
          {['Kuala Lumpur', 'Selangor', 'Johor', 'Penang', 'Perak', 'Sabah', 'Sarawak', 'Kedah', 'Kelantan', 'Melaka', 'Negeri Sembilan', 'Pahang', 'Perlis', 'Terengganu', 'Putrajaya', 'Labuan'].map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ my: 1 }} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth label="Referral Code (optional)"
          value={form.referralCode || refCode}
          onChange={update('referralCode')}
          placeholder="Enter referral code or phone number"
          helperText="Enter a referral code, agent code, or phone number of your referrer"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl>
          <FormLabel sx={{ fontSize: '0.85rem', mb: 1 }}>Are you a Malaysian citizen? *</FormLabel>
          <RadioGroup row value={form.isCitizen ? 'yes' : 'no'} onChange={(e) => setForm({ ...form, isCitizen: e.target.value === 'yes' })}>
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>
      </Grid>
    </Grid>
  );

  const renderStep1 = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Select a product</Typography>
      </Grid>
      {availableProducts.map((p) => (
        <Grid item xs={12} md={6} key={p.id}>
          <Card
            variant="outlined"
            onClick={() => setForm({ ...form, selectedProduct: String(p.id) })}
            sx={{
              cursor: 'pointer', borderRadius: 2,
              border: form.selectedProduct === String(p.id) ? '2px solid #00B894' : '1px solid #E0E0E0',
              backgroundColor: form.selectedProduct === String(p.id) ? '#F0FFF4' : 'white',
              '&:hover': { borderColor: '#00B894' }, transition: 'all 0.2s',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{p.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{p.bank}</Typography>
                </Box>
                {form.selectedProduct === String(p.id) && <CheckCircleIcon sx={{ color: '#00B894' }} />}
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Profit Rate</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{p.rate}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Max Amount</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>RM {p.maxAmount.toLocaleString()}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
      <Grid item xs={12} md={6}>
        <TextField fullWidth label="Financing Amount *" value={form.amount} onChange={update('amount')}
          InputProps={{ startAdornment: <InputAdornment position="start">RM</InputAdornment> }}
          placeholder="10,000"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField fullWidth select label="Tenure *" value={form.tenure} onChange={update('tenure')}>
          {[1, 2, 3, 4, 5, 6, 7].map((y) => (
            <MenuItem key={y} value={String(y)}>{y} year{y > 1 ? 's' : ''}</MenuItem>
          ))}
        </TextField>
      </Grid>
    </Grid>
  );

  const renderStep2 = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Please upload the required documents. Supported formats: PDF, JPG, PNG.
        </Alert>
      </Grid>
      <Grid item xs={12} md={6}>
        <FileUpload label="IC / MyKad (Front & Back) *" maxFiles={2} />
      </Grid>
      <Grid item xs={12} md={6}>
        <FileUpload label="Latest 3 Months Payslip *" maxFiles={3} />
      </Grid>
      <Grid item xs={12} md={6}>
        <FileUpload label="Latest 3 Months Bank Statement *" maxFiles={3} />
      </Grid>
      <Grid item xs={12} md={6}>
        <FileUpload label="EPF Statement (Optional)" maxFiles={1} />
      </Grid>
      <Grid item xs={12}>
        <FileUpload label="Additional Supporting Documents" maxFiles={5} />
      </Grid>
    </Grid>
  );

  const renderStep3 = () => {
    const selectedProd = availableProducts.find((p) => String(p.id) === form.selectedProduct);
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Alert severity="success" sx={{ borderRadius: 2 }}>
            Please review your application details before submitting.
          </Alert>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#1B365D' }}>Personal Details</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Full Name</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{form.fullName || '-'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">IC Number</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{form.icNumber || '-'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{form.email || '-'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>+6{form.phone || '-'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Monthly Income</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>RM {form.monthlyIncome || '-'}</Typography>
                </Box>
                {form.referralCode && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Referral Code</Typography>
                    <Chip label={form.referralCode} size="small" color="secondary" />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#1B365D' }}>Product Details</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Product</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedProd?.name || '-'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Bank</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedProd?.bank || '-'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Amount</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>RM {form.amount || '-'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Tenure</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{form.tenure} year(s)</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={<Checkbox checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} />}
            label={<Typography variant="body2">I have read and consent to the processing of my personal data in accordance with the Privacy Policy.</Typography>}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#1B365D', mb: 1 }}>Apply Now</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Complete the form below to submit your application
      </Typography>

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          {activeStep === 0 && renderStep0()}
          {activeStep === 1 && renderStep1()}
          {activeStep === 2 && renderStep2()}
          {activeStep === 3 && renderStep3()}
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => s - 1)} sx={{ textTransform: 'none' }}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            if (activeStep === steps.length - 1) {
              handleSubmitApplication();
            } else {
              setActiveStep((s) => s + 1);
            }
          }}
          sx={{ textTransform: 'none', borderRadius: 2, px: 4, backgroundColor: '#1B365D' }}
        >
          {activeStep === steps.length - 1 ? 'Submit Application' : 'Next'}
        </Button>
      </Box>

      <Dialog open={showSuccess} maxWidth="sm" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 5 }}>
          <CheckCircleIcon sx={{ fontSize: 72, color: '#00B894', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Application Submitted!</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your application has been successfully submitted. You will receive a confirmation email shortly.
          </Typography>
          <Chip
            label={`Case Reference: ${submittedCase?.id || '—'}`}
            sx={{ fontWeight: 600, fontSize: '0.9rem', py: 2, px: 1 }}
          />
          {form.referralCode && (
            <Alert severity="info" sx={{ mt: 3, borderRadius: 2, textAlign: 'left' }}>
              Your referral code <strong>{form.referralCode}</strong> has been recorded. Both you and your referrer may be eligible for token rewards!
            </Alert>
          )}
          {isAuthenticated && user?.role === 'free_user' && tokenAwarded > 0 && (
            <Alert severity="success" sx={{ mt: 2, borderRadius: 2, textAlign: 'left' }}>
              You earned <strong>{tokenAwarded} tokens</strong> for submitting this application! Share your referral link to earn more.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button variant="contained" sx={{ borderRadius: 2, textTransform: 'none', backgroundColor: '#1B365D' }}
            onClick={() => { setShowSuccess(false); setActiveStep(0); navigate('/my-applications'); }}>
            Track My Application
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
